import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAsset } from '../services/assetService';
import { scanService } from '../services/api';
import { updateOnboardingState } from '../services/profileService';
import {
  trackWizardStart, trackWizardStep, trackWizardSkip, trackWizardComplete,
  trackAssetAdded, trackScanStart, trackScanComplete, trackScanError,
  trackViewResults, trackCtaClick
} from '../utils/onboardingAnalytics';
import { getRiskContext } from '../utils/riskBands';
import './OnboardingWizard.css';

const DOMAIN_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9-]*\.)+[a-zA-Z]{2,}$/;
const IP_REGEX = /^\d{1,3}(\.\d{1,3}){3}$/;

const STEP_NAMES = ['welcome', 'add_asset', 'quick_scan', 'complete'];

function OnboardingWizard({ onComplete }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(() =>
    parseInt(localStorage.getItem('driftalarm_onboardingStep')) || 1
  );
  const [domain, setDomain] = useState('');
  const [assetData, setAssetData] = useState(null);
  const [scanId, setScanId] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [skippedSteps, setSkippedSteps] = useState([]);
  const [completedSteps, setCompletedSteps] = useState([]);
  const pollRef = useRef(null);
  const scanTriggered = useRef(false);
  const scanStartTime = useRef(null);
  const wizardStartTracked = useRef(false);

  const recordStepSkip = useCallback((stepName) => {
    setSkippedSteps(prev => prev.includes(stepName) ? prev : [...prev, stepName]);
  }, []);

  const recordStepComplete = useCallback((stepName) => {
    setCompletedSteps(prev => prev.includes(stepName) ? prev : [...prev, stepName]);
  }, []);

  const clearLegacyLocalStorage = () => {
    // No-op safety net for browsers carrying pre-PRD-64 keys. Remove after one release.
    localStorage.removeItem('driftalarm_onboardingComplete');
    localStorage.removeItem('driftalarm_onboardingStep');
    localStorage.removeItem('driftalarm_onboardingAssetId');
  };

  // Track wizard start (once)
  useEffect(() => {
    if (!wizardStartTracked.current) {
      wizardStartTracked.current = true;
      trackWizardStart();
    }
  }, []);

  // Track step transitions
  const goToStep = useCallback((newStep) => {
    trackWizardStep(newStep, STEP_NAMES[newStep - 1]);
    setStep(newStep);
  }, []);

  // Persist step to localStorage
  useEffect(() => {
    localStorage.setItem('driftalarm_onboardingStep', step);
  }, [step]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Auto-detect asset type
  const detectedType = domain.trim()
    ? IP_REGEX.test(domain.trim()) ? 'ip' : 'domain'
    : null;

  const isValidInput = domain.trim() && (
    DOMAIN_REGEX.test(domain.trim()) || IP_REGEX.test(domain.trim())
  );

  // Strip protocol from input
  const handleDomainChange = (e) => {
    let val = e.target.value;
    val = val.replace(/^https?:\/\//i, '').replace(/\/.*$/, '').trim();
    setDomain(val);
    setError(null);
  };

  // Step 2: Add asset
  const handleAddAsset = async () => {
    if (!isValidInput) {
      setError('Please enter a valid domain or IP address');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const assetType = IP_REGEX.test(domain.trim()) ? 'ip' : 'domain';
      const result = await createAsset(assetType, domain.trim());
      setAssetData(result);
      localStorage.setItem('driftalarm_onboardingAssetId', result.assetId);
      trackAssetAdded(assetType, result.assetId);
      recordStepComplete('add_asset');
      goToStep(3);
    } catch (err) {
      setError(err.message || 'Failed to add asset');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Trigger scan
  const triggerScan = useCallback(async () => {
    if (scanTriggered.current) return;
    scanTriggered.current = true;
    scanStartTime.current = Date.now();
    try {
      setLoading(true);
      setError(null);
      const target = assetData?.asset?.value || domain.trim();
      const result = await scanService.startFastScan(target);
      const newScanId = result.scanId;
      setScanId(newScanId);
      trackScanStart(target, newScanId);

      // Start polling
      pollRef.current = setInterval(async () => {
        try {
          const status = await scanService.getScanStatus(newScanId);
          if (status.status === 'completed' || status.status === 'complete') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            try {
              const scanData = await scanService.getScanResult(newScanId);
              setScanResult(scanData);
              const r = scanData.result || scanData;
              const metrics = {
                subdomains: r.subdomainCount || r.subdomains?.length || 0,
                ports: r.openPortCount || r.ports?.length || 0,
                findings: r.findingsCount || r.findings?.length || r.vulnerabilities?.length || 0
              };
              const riskScore = r.riskScore ?? null;
              const duration = scanStartTime.current ? Date.now() - scanStartTime.current : null;
              trackScanComplete(newScanId, metrics.subdomains, metrics.ports, metrics.findings, riskScore, duration);
            } catch {
              setScanResult({ completed: true });
            }
            setLoading(false);
          } else if (status.status === 'failed' || status.status === 'error') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            const errMsg = 'Scan failed \u2014 you can try again from the dashboard';
            setError(errMsg);
            trackScanError(errMsg);
            setLoading(false);
          }
        } catch {
          // Continue polling on transient errors
        }
      }, 5000);
    } catch (err) {
      const errMsg = err.message || 'Failed to start scan';
      setError(errMsg);
      trackScanError(errMsg);
      setLoading(false);
      scanTriggered.current = false;
    }
  }, [assetData, domain]);

  // Auto-trigger scan when entering step 3
  useEffect(() => {
    if (step === 3 && assetData && !scanId) {
      triggerScan();
    }
  }, [step, assetData, scanId, triggerScan]);

  // PRD-64: two intentional dismiss paths, both persisted server-side.
  //   'skip_for_now' / 'close_icon' → sets dismissedAt (re-openable via Settings)
  //   'dont_show_again'             → sets completedAt (permanent until Replay)
  const handleDismiss = async (kind) => {
    trackWizardSkip(step, kind);
    if (pollRef.current) clearInterval(pollRef.current);
    const nowIso = new Date().toISOString();
    const patch = (kind === 'dont_show_again')
      ? { completedAt: nowIso, closedFromStep: step, lastStep: step, skippedSteps, completedSteps }
      : { dismissedAt: nowIso, closedFromStep: step, lastStep: step, skippedSteps, completedSteps };
    try {
      await updateOnboardingState(patch);
    } catch (err) {
      console.error('Failed to persist onboarding dismissal:', err);
    }
    clearLegacyLocalStorage();
    onComplete();
  };

  const handleComplete = async (navigateTo) => {
    const metrics = getMetrics();
    trackWizardComplete(!!assetData, !!scanResult, metrics.findings);
    if (pollRef.current) clearInterval(pollRef.current);
    const nowIso = new Date().toISOString();
    const finalCompleted = completedSteps.includes(STEP_NAMES[step - 1])
      ? completedSteps
      : [...completedSteps, STEP_NAMES[step - 1]];
    try {
      await updateOnboardingState({
        completedAt: nowIso,
        lastStep: step,
        closedFromStep: step,
        completedSteps: finalCompleted,
        skippedSteps,
      });
    } catch (err) {
      console.error('Failed to persist onboarding completion:', err);
    }
    clearLegacyLocalStorage();
    onComplete();
    if (navigateTo) {
      setTimeout(() => navigate(navigateTo), 100);
    }
  };

  // Extract scan metrics from result
  const getMetrics = () => {
    if (!scanResult) return { subdomains: 0, ports: 0, findings: 0, technologies: 0, riskScore: null };
    const r = scanResult.result || scanResult;
    const techCount = (r.technologies?.length || 0) +
      (r.httpx?.technologies?.length || 0) +
      (r.webTech?.technologies?.length || 0);
    return {
      subdomains: r.subdomainCount || r.subdomains?.length || 0,
      ports: r.openPortCount || r.ports?.length || 0,
      findings: r.findingsCount || r.findings?.length || r.vulnerabilities?.length || 0,
      technologies: techCount || 0,
      riskScore: r.riskScore ?? null
    };
  };

  // Get top findings from result with description
  const getTopFindings = () => {
    if (!scanResult) return [];
    const r = scanResult.result || scanResult;
    const findings = r.findings || r.vulnerabilities || [];
    return findings.slice(0, 3).map(f => ({
      severity: (f.severity || f.risk || 'info').toLowerCase(),
      title: f.title || f.name || 'Finding',
      description: f.description || f.info || ''
    }));
  };

  // Check if any critical/high findings exist
  const hasCriticalOrHigh = () => {
    const findings = getTopFindings();
    return findings.some(f => f.severity === 'critical' || f.severity === 'high');
  };

  // Render progress dots
  const renderProgress = () => (
    <div className="onboarding-progress">
      {[1, 2, 3, 4].map(i => (
        <div
          key={i}
          className="onboarding-progress-dot"
          data-active={String(i === step)}
          data-completed={String(i < step)}
        />
      ))}
    </div>
  );

  const metrics = getMetrics();

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-card">
        <button
          className="onboarding-close"
          onClick={() => handleDismiss('close_icon')}
          aria-label="Close onboarding tour"
          title="Close (you can reopen from Settings)"
        >
          &times;
        </button>
        {renderProgress()}

        {/* Step 1: Welcome */}
        {step === 1 && (
          <div className="onboarding-step onboarding-welcome">
            <h2>Welcome to DriftAlarm</h2>
            <p>We'll help you set up continuous attack surface monitoring in 2 minutes.</p>
            <div className="onboarding-value-props">
              <div className="onboarding-value-prop">
                <div className="onboarding-value-prop-title">Discover</div>
                <div className="onboarding-value-prop-desc">Find all your external-facing assets</div>
              </div>
              <div className="onboarding-value-prop">
                <div className="onboarding-value-prop-title">Monitor</div>
                <div className="onboarding-value-prop-desc">Detect changes before attackers do</div>
              </div>
              <div className="onboarding-value-prop">
                <div className="onboarding-value-prop-title">Protect</div>
                <div className="onboarding-value-prop-desc">Get alerts when your surface shifts</div>
              </div>
            </div>
            <button
              className="onboarding-btn-primary"
              onClick={() => {
                recordStepComplete('welcome');
                goToStep(2);
              }}
            >
              Let's get started
            </button>
            <div className="onboarding-step1-dismiss-row">
              <button className="onboarding-skip" onClick={() => handleDismiss('skip_for_now')}>
                Skip for now
              </button>
              <button className="onboarding-skip" onClick={() => handleDismiss('dont_show_again')}>
                Don't show again
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Add Asset */}
        {step === 2 && (
          <div className="onboarding-step onboarding-add-asset">
            <h2>What do you want to monitor?</h2>
            <p>Enter a domain or IP address to start monitoring.</p>
            <div className="onboarding-input-group">
              <input
                type="text"
                className="onboarding-input"
                placeholder="Enter a domain or IP address"
                value={domain}
                onChange={handleDomainChange}
                onKeyDown={(e) => e.key === 'Enter' && isValidInput && handleAddAsset()}
                autoFocus
              />
              {detectedType && domain.trim() && (
                <span className="onboarding-type-badge">
                  {detectedType === 'ip' ? 'IP address detected' : 'Domain detected'}
                </span>
              )}
              {error && <div className="onboarding-error">{error}</div>}
            </div>
            <button
              className="onboarding-btn-primary"
              onClick={handleAddAsset}
              disabled={!isValidInput || loading}
            >
              {loading ? 'Adding...' : 'Add & Continue'}
            </button>
            <button
              className="onboarding-skip"
              onClick={() => {
                trackWizardSkip(2, 'skip_step');
                recordStepSkip('add_asset');
                goToStep(4);
              }}
            >
              Skip
            </button>
          </div>
        )}

        {/* Step 3: Quick Scan — Enhanced with risk score, context, CTAs */}
        {step === 3 && (
          <div className="onboarding-step onboarding-scan">
            {!scanResult ? (
              <>
                <h2>Scanning {domain || 'your asset'}...</h2>
                <div className="onboarding-scan-progress">
                  <div className="onboarding-scan-spinner" />
                  <div className="onboarding-scan-status">
                    This usually takes 1-2 minutes
                  </div>
                </div>
                {error && <div className="onboarding-error">{error}</div>}
              </>
            ) : (
              <>
                {/* Risk Score */}
                {metrics.riskScore !== null && (
                  <div className="onboarding-risk-section">
                    <h2>Your Security Risk Score</h2>
                    <div className="onboarding-risk-score" style={{ color: getRiskContext(metrics.riskScore).color }}>
                      {metrics.riskScore}<span className="onboarding-risk-score-max">/100</span>
                    </div>
                    <div className="onboarding-risk-label" style={{ color: getRiskContext(metrics.riskScore).color }}>
                      {getRiskContext(metrics.riskScore).label}
                    </div>
                    <p className="onboarding-risk-context">
                      {getRiskContext(metrics.riskScore).message}
                    </p>
                  </div>
                )}
                {metrics.riskScore === null && <h2>Scan complete</h2>}

                {/* What We Found */}
                {getTopFindings().length > 0 && (
                  <div className="onboarding-findings-section">
                    <h3 className="onboarding-section-title">What We Found</h3>
                    {hasCriticalOrHigh() && (
                      <div className="onboarding-urgent-label">Needs immediate attention</div>
                    )}
                    {!hasCriticalOrHigh() && metrics.findings > 0 && (
                      <div className="onboarding-good-label">No critical issues found</div>
                    )}
                    <div className="onboarding-results-preview">
                      {getTopFindings().map((f, i) => (
                        <div key={i} className={`onboarding-finding-row ${(f.severity === 'critical' || f.severity === 'high') ? 'onboarding-finding-urgent' : ''}`}>
                          <span className="onboarding-finding-severity" data-severity={f.severity}>
                            {f.severity}
                          </span>
                          <div className="onboarding-finding-details">
                            <span className="onboarding-finding-title">{f.title}</span>
                            {f.description && (
                              <span className="onboarding-finding-desc">{f.description}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {getTopFindings().length === 0 && (
                  <div className="onboarding-findings-section">
                    <div className="onboarding-good-label">Looking good &mdash; no critical issues found</div>
                  </div>
                )}

                {/* Your Attack Surface */}
                <div className="onboarding-surface-section">
                  <h3 className="onboarding-section-title">Your Attack Surface</h3>
                  <div className="onboarding-surface-items">
                    <div className="onboarding-surface-item">
                      <span className="onboarding-surface-value">{metrics.subdomains}</span>
                      <span className="onboarding-surface-label">
                        subdomains discovered
                        {metrics.subdomains > 3 && <span className="onboarding-surface-note"> &mdash; more than you might expect</span>}
                      </span>
                    </div>
                    <div className="onboarding-surface-item">
                      <span className="onboarding-surface-value">{metrics.ports}</span>
                      <span className="onboarding-surface-label">
                        open ports found
                        {metrics.ports > 5 && <span className="onboarding-surface-note"> &mdash; each is a potential entry point for attackers</span>}
                      </span>
                    </div>
                    <div className="onboarding-surface-item">
                      <span className="onboarding-surface-value">{metrics.technologies}</span>
                      <span className="onboarding-surface-label">technologies detected</span>
                    </div>
                  </div>
                </div>

                {/* Contextual CTAs */}
                <div className="onboarding-step3-ctas">
                  <button
                    className="onboarding-btn-primary"
                    onClick={() => {
                      trackViewResults(scanId);
                      trackCtaClick('view_report');
                      window.open(`/scan-results/${scanId}`, '_blank');
                    }}
                  >
                    View Full Report
                  </button>
                  <button
                    className="onboarding-btn-secondary"
                    onClick={() => {
                      trackCtaClick('enable_alerts');
                      handleComplete('/settings');
                    }}
                  >
                    Enable Drift Alerts
                  </button>
                  <button
                    className="onboarding-skip"
                    onClick={() => {
                      recordStepComplete('quick_scan');
                      goToStep(4);
                    }}
                  >
                    Continue to Dashboard
                  </button>
                </div>
              </>
            )}
            {!scanResult && (
              <>
                <button className="onboarding-btn-primary" onClick={() => goToStep(4)} style={{ marginTop: 16 }}>
                  Continue
                </button>
                <button
                  className="onboarding-skip"
                  onClick={() => {
                    trackWizardSkip(3, 'skip_step');
                    recordStepSkip('quick_scan');
                    goToStep(4);
                  }}
                >
                  Skip
                </button>
              </>
            )}
          </div>
        )}

        {/* Step 4: Enhanced completion with action CTAs + trial countdown */}
        {step === 4 && (
          <div className="onboarding-step onboarding-complete">
            <h2>Great work &mdash; monitoring is active</h2>
            <div className="onboarding-summary-list">
              {assetData && (
                <div className="onboarding-summary-item">
                  <span className="onboarding-summary-icon" data-type="done">&#x2713;</span>
                  <span className="onboarding-summary-text">
                    {domain || 'Asset'} added as a monitored asset
                  </span>
                </div>
              )}
              {scanResult && (
                <div className="onboarding-summary-item">
                  <span className="onboarding-summary-icon" data-type="done">&#x2713;</span>
                  <span className="onboarding-summary-text">
                    Quick scan complete &mdash; {metrics.findings} findings discovered
                  </span>
                </div>
              )}
              <div className="onboarding-summary-item">
                <span className="onboarding-summary-icon" data-type="info">i</span>
                <span className="onboarding-summary-text">
                  Full discovery scan will run automatically
                </span>
              </div>
              <div className="onboarding-summary-item">
                <span className="onboarding-summary-icon" data-type="info">i</span>
                <span className="onboarding-summary-text">
                  Daily vulnerability scans keep you protected
                </span>
              </div>
            </div>

            <p className="onboarding-step4-prompt">What would you like to do next?</p>

            <div className="onboarding-step4-actions">
              <button
                className="onboarding-btn-primary"
                onClick={() => {
                  trackCtaClick('enable_alerts');
                  handleComplete('/settings');
                }}
              >
                Enable Drift Alerts
              </button>
              {scanId && (
                <button
                  className="onboarding-btn-secondary"
                  onClick={() => {
                    trackCtaClick('view_report');
                    handleComplete(`/scan-results/${scanId}`);
                  }}
                >
                  View Full Report
                </button>
              )}
              <button
                className="onboarding-btn-secondary"
                onClick={() => {
                  trackCtaClick('explore_dashboard');
                  handleComplete();
                }}
              >
                Explore Dashboard
              </button>
            </div>

            <p className="onboarding-trial-countdown">
              You have 30 days to explore DriftAlarm &mdash; make the most of your trial.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default OnboardingWizard;
